import { useEffect, useState } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, Loader2, X } from 'lucide-react';
import {
  BATIMENT_STATUTS,
  BATIMENT_TYPES,
  createBatimentSchema,
  type CreateBatimentInput,
  type FormSchemaDocument,
} from '@mygmao/shared';
import { api, extractErrorMessage } from '../../lib/api';
import { ModalPortal } from '../ModalPortal';
import { DynamicFormRenderer } from '../DynamicFormRenderer';

/**
 * Modal de création / édition d'un bâtiment (US-BAT-02).
 *
 * Champs de base codés en dur (code, nom, type, statut, adresse, surface,
 * nombre d'étages, responsable). Les champs avancés viendront via Form
 * Builder (US-ADM-08) — le backend valide déjà les `custom_fields` mais
 * l'UI Form Builder rendering est différée à la livraison du schéma actif.
 */
export function BatimentFormModal({
  batimentId,
  onClose,
  onSaved,
}: {
  batimentId?: string;
  onClose(): void;
  onSaved(): void;
}) {
  const isEdit = !!batimentId;
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [activeSchema, setActiveSchema] = useState<FormSchemaDocument | null>(null);
  const [customValues, setCustomValues] = useState<Record<string, unknown>>({});

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateBatimentInput>({
    resolver: zodResolver(createBatimentSchema) as unknown as Resolver<CreateBatimentInput>,
    defaultValues: {
      type: 'principal',
      statut: 'en_service',
      nombre_etages: 0,
    },
  });

  // Charge le schéma Form Builder actif pour l'entité 'batiment' (US-ADM-08).
  // Endpoint public (auth seulement) — accessible aux non-admins qui créent
  // des bâtiments. Si aucun schéma n'est actif, on rend uniquement les champs
  // de base.
  useEffect(() => {
    api
      .get<{ data: { schema: FormSchemaDocument } | null }>('/form-schemas/active/batiment')
      .then((res) => setActiveSchema(res.data.data?.schema ?? null))
      .catch(() => setActiveSchema(null));
  }, []);

  useEffect(() => {
    if (!batimentId) {
      // En création : suggère un code basé sur le préfixe tenant (Phase 6 +
      // pré-Phase 9 — `numbering.batiment_prefix`).
      api
        .get<{ data: { code: string } }>('/numbering/suggest?kind=batiment')
        .then((res) => reset((prev) => ({ ...prev, code: res.data.data.code }) as typeof prev))
        .catch(() => {
          // Silencieux : l'utilisateur pourra saisir manuellement.
        });
      return;
    }
    (async () => {
      try {
        const res = await api.get<{
          data: CreateBatimentInput & { custom_fields?: Record<string, unknown> };
        }>(`/batiments/${batimentId}`);
        reset({
          code: res.data.data.code,
          nom: res.data.data.nom,
          type: res.data.data.type,
          adresse: res.data.data.adresse ?? '',
          surface_totale: res.data.data.surface_totale ?? undefined,
          nombre_etages: res.data.data.nombre_etages,
          statut: res.data.data.statut,
          responsable_id: res.data.data.responsable_id ?? undefined,
        });
        setCustomValues(res.data.data.custom_fields ?? {});
      } catch (err) {
        setErrorMessage(extractErrorMessage(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [batimentId, reset]);

  async function onSubmit(values: CreateBatimentInput) {
    setSaving(true);
    setErrorMessage(null);
    try {
      const payload = {
        ...values,
        adresse: values.adresse || null,
        surface_totale: values.surface_totale || null,
        // Champs Form Builder injectés au moment de la soumission. Le backend
        // valide contre le schéma actif (validateAgainstActiveSchema).
        custom_fields: customValues,
      };
      if (isEdit) {
        await api.patch(`/batiments/${batimentId}`, payload);
      } else {
        await api.post('/batiments', payload);
      }
      onSaved();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!batimentId) return;
    if (!confirm('Supprimer ce bâtiment ? (soft delete — uniquement si aucun étage rattaché)'))
      return;
    setSaving(true);
    try {
      await api.delete(`/batiments/${batimentId}`);
      onSaved();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-2xl w-full shadow-2xl max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
            <h2 className="text-lg font-semibold text-gray-900">
              {isEdit ? 'Modifier le bâtiment' : 'Nouveau bâtiment'}
            </h2>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {errorMessage && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center p-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
              </div>
            ) : (
              <form id="batiment-form" onSubmit={handleSubmit(onSubmit)} className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Code *" error={errors.code?.message}>
                    <input
                      {...register('code')}
                      placeholder="BAT-001"
                      className={inputClass + ' uppercase'}
                    />
                  </Field>
                  <Field label="Nom *" error={errors.nom?.message}>
                    <input {...register('nom')} className={inputClass} />
                  </Field>
                  <Field label="Type *" error={errors.type?.message}>
                    <select {...register('type')} className={inputClass}>
                      {BATIMENT_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Statut *" error={errors.statut?.message}>
                    <select {...register('statut')} className={inputClass}>
                      {BATIMENT_STATUTS.map((s) => (
                        <option key={s} value={s}>
                          {s.replace(/_/g, ' ')}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Adresse" error={errors.adresse?.message} className="md:col-span-2">
                    <textarea {...register('adresse')} rows={2} className={inputClass} />
                  </Field>
                  <Field label="Surface totale (m²)" error={errors.surface_totale?.message}>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      {...register('surface_totale', {
                        setValueAs: (v) => (v === '' || v === null ? null : Number(v)),
                      })}
                      className={inputClass}
                    />
                  </Field>
                  <Field label="Nombre d'étages" error={errors.nombre_etages?.message}>
                    <input
                      type="number"
                      min="0"
                      {...register('nombre_etages', { valueAsNumber: true })}
                      className={inputClass}
                    />
                  </Field>
                </div>

                {activeSchema && activeSchema.fields.length > 0 && (
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                      Champs personnalisés
                    </h3>
                    <DynamicFormRenderer
                      schema={activeSchema}
                      values={customValues}
                      onChange={setCustomValues}
                      disabled={saving}
                    />
                  </div>
                )}
              </form>
            )}
          </div>

          <div className="flex items-center justify-between p-4 border-t border-gray-200 flex-shrink-0">
            <div>
              {isEdit && (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="text-sm text-red-600 hover:text-red-700 font-medium"
                  disabled={saving}
                >
                  Supprimer
                </button>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                form="batiment-form"
                disabled={saving || loading}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {isEdit ? 'Enregistrer' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

function Field({
  label,
  error,
  children,
  className,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}
